import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "providers/auth";
import { useToast } from "components/ui/Toast/use-toast";
import { logError } from "utility/Function";
import {
  coachFetch,
  CoachAuthError,
  reportChatViewed,
} from "apps/user/learner/api/coach";
import { authenticationFailed } from "providers/apollo-client";
import useOriConnection, {
  ConnectionState,
  OriQaConnectionParams,
  SendOptions,
} from "apps/user/learner/hooks/use-ori-connection";
import {
  ChatStatusResponse,
  Message,
  MessageAgenda,
  OriInboundEvent,
} from "apps/user/learner/pages/chat/types";
import {
  consumeOriStarterMessage,
  hasPendingOriStarterText,
  recordStarterSent,
} from "apps/user/learner/lib/ori-starter-message";
import {
  TAP_IN_ENTRY_POINTS,
  TapInEntryPoint,
  TapInWire,
} from "apps/user/learner/lib/tap-in";
import {
  fetchConversation,
  fetchConversations,
  ConversationDetailMessage,
} from "apps/user/learner/api/coach-history";
import { trackEvent } from "utility/mixpanel";
import useFeatureFlags, {
  FEATURE_FLAGS,
} from "apps/user/learner/hooks/use-feature-flags";
import { parseOptions } from "apps/user/learner/pages/chat/message-options";

type InputSource = "voice" | "keyboard" | "starter";

const GREETING_TRIGGER = "__START__";
const STARTER_OPTIONS_CAPABILITY = "starter_options";
export const FOCUS_REFETCH_DEBOUNCE_MS = 500;

// Coach's typed discriminator for the per-user lock rejection.
const LOCK_REJECTION_CODE = "processing_in_progress";

function isLockRejection(event: OriInboundEvent): boolean {
  return event.type === "error" && event.code === LOCK_REJECTION_CODE;
}

const BUSY_RETRY_BASE_MS = 1500;
const BUSY_RETRY_MAX_ATTEMPTS = 3;
const INTERRUPTED_MESSAGE = "Ori's response was interrupted. Please try again.";

// Client-side silence watchdog for an in-flight turn: a soft "Still thinking…"
// line, then a stalled toast. Never closes the socket — Coach's own turn
// budget ends a genuinely dead turn.
const WATCHDOG_STILL_THINKING_MS = 30_000;
const WATCHDOG_STALLED_MS = 90_000;
const STILL_THINKING_TEXT = "Still thinking…";
const STALLED_MESSAGE =
  "Ori is taking longer than usual — you can wait or try again";

const BUSY_MESSAGE: Record<Exclude<BusyPhase, "idle">, string> = {
  retrying: "Ori is finishing a reply on your other device…",
  "could-not-send": "Couldn't send just yet — Ori was busy. Try sending again.",
};

// A turn moves idle → sending (awaiting first reply) → streaming (reply
// arriving) → idle. `isSending` spans the whole turn; `isAwaitingReply` is
// just the pre-reply wait.
type TurnState = "idle" | "sending" | "streaming";

// Cross-device send-lock state: retrying after a lock rejection, or exhausted
// the retry budget (text retained, awaiting a manual resend).
type BusyPhase = "idle" | "retrying" | "could-not-send";

// Last server-assigned message id; the optimistic user bubble has none.
function lastServerMessageId(messages: Message[]): string | null {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const id = messages[i].message_id;
    if (id) return id;
  }
  return null;
}

function trackMessageReceived(message: Message, index: number): void {
  trackEvent("Coach Message Received", {
    "Message ID": message.message_id ?? null,
    "Message Index": index,
    "Has Embedded UI": !!message.embedded_ui,
    "Has Recommendations": !!message.embedded_ui?.recommendations?.length,
    "Configuration ID": message.configuration_id ?? null,
    "Configuration Name": message.configuration_name ?? null,
    "Thread ID": message.thread_id ?? null,
    "Thread Lifecycle": message.thread_lifecycle ?? null,
    "Conversation Version ID": message.conversation_version_id ?? null,
    "Conversation Blueprint Name": message.conversation_blueprint_name ?? null,
    "Conversation Version Number": message.conversation_version_number ?? null,
  });
}

function toMessages(status: ChatStatusResponse): Message[] {
  return (status.messages ?? []).map((m) => ({
    role: m.role,
    content: m.content,
    message_id: m.message_id,
    embedded_ui: m.embedded_ui,
    ...parseOptions(m),
    user_feedback: m.user_feedback ?? null,
  }));
}

// A resumed bounded (QA) thread seeds its live transcript from the
// conversation detail endpoint, chronologically ordered.
function detailToMessages(
  detail: ConversationDetailMessage[],
  agenda?: MessageAgenda
): Message[] {
  return [...detail]
    .sort((a, b) => a.created_at.localeCompare(b.created_at))
    .map((m) => ({
      role: m.role,
      content: m.content,
      message_id: m.id,
      embedded_ui: m.embedded_ui ?? undefined,
      ...parseOptions(m),
      user_feedback: m.user_feedback ?? null,
      agenda,
    }));
}

interface UseOriChatReturn {
  messages: Message[];
  /** Thread of Ori's latest reply, else of the loaded history. */
  threadId: string | null;
  sendMessage: (text: string, source?: InputSource) => void;
  /** Server signal: has this user ever sent Ori a real message? */
  hasEverMessaged: boolean;
  /** A stashed entry-point prompt waits to be sent on connect. */
  hasPendingEntryStarter: boolean;
  isLoadingHistory: boolean;
  isSending: boolean;
  isAwaitingReply: boolean;
  busyMessage: string | null;
  // Transient status line for the typing-indicator slot: tool activity from
  // Coach, or the watchdog's "Still thinking…". Never part of `messages`.
  activityStatus: string | null;
  connectionState: ConnectionState;
  isConnected: boolean;
  /** The server says this conversation opens with a greeting and the socket
   *  carrying it hasn't failed — so an empty transcript is "not yet", and the
   *  welcome empty state must stay hidden. */
  isGreetingPending: boolean;
  reconnect: () => void;
}

interface UseOriChatOptions {
  // When false, the WebSocket stays closed and no starter/greeting is sent.
  // Callers gate this on disclaimer acceptance so nothing reaches Coach — and
  // no starter prompt is consumed — before the user has consented.
  enabled?: boolean;
  // QA live-template-testing session (ori_qa feature flag). Passed straight
  // through to useOriConnection, which rebinds the WS ticket request and
  // reconnects when it changes.
  qa?: OriQaConnectionParams | null;
}

export default function useOriChat(
  options: UseOriChatOptions = {}
): UseOriChatReturn {
  const { enabled = true, qa = null } = options;
  // Stable primitive key for the QA session (empty when not in QA). Drives the
  // "fresh, isolated QA thread" behaviour below without depending on the `qa`
  // object's identity.
  const qaKey = qa ? `${qa.configId}:${qa.threadId}` : "";
  const qaThreadId = qa?.threadId ?? null;
  const { authToken } = useAuth();
  const { openToast } = useToast();

  const [messages, setMessages] = useState<Message[]>([]);
  const [threadId, setThreadId] = useState<string | null>(null);
  const { isEnabled, resolved: flagsResolved } = useFeatureFlags();
  const startersEnabled = isEnabled(FEATURE_FLAGS.ORI_DYNAMIC_STARTERS);
  // Fail-closed default: absent server field reads as a returning user, so
  // the first-time-only starter pills stay hidden.
  const [hasEverMessaged, setHasEverMessaged] = useState(true);
  const [hasPendingEntryStarter, setHasPendingEntryStarter] = useState(
    hasPendingOriStarterText
  );
  const [turnState, setTurnState] = useState<TurnState>("idle");
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [busyPhase, setBusyPhase] = useState<BusyPhase>("idle");
  const [activityStatus, setActivityStatus] = useState<string | null>(null);

  // Cross-device lock-rejection auto-retry. The pending text and attempt count
  // live in refs so the retry timer survives re-renders; the timeout is cleared
  // on unmount and whenever the turn genuinely resumes.
  const pendingRetryMessageRef = useRef<{
    text: string;
    tapIn?: TapInWire;
  } | null>(null);
  const busyAttemptRef = useRef(0);
  const busyRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // sendMessage is created below by useOriConnection; the retry timer reads it
  // through a ref to avoid the onEvent → connection → onEvent cycle.
  const sendMessageRef = useRef<
    (
      message: string,
      clientLastMessageId?: string | null,
      options?: SendOptions
    ) => void
  >(() => {});

  const clearBusyRetryTimer = useCallback(() => {
    if (busyRetryTimerRef.current !== null) {
      clearTimeout(busyRetryTimerRef.current);
      busyRetryTimerRef.current = null;
    }
  }, []);

  // A real turn is resuming (lock freed). Drop the busy state and stop retrying.
  const clearBusyState = useCallback(() => {
    clearBusyRetryTimer();
    pendingRetryMessageRef.current = null;
    busyAttemptRef.current = 0;
    setBusyPhase("idle");
  }, [clearBusyRetryTimer]);

  // openToast is a fresh ref each render; pin it so effects don't churn on it.
  const openToastRef = useRef(openToast);
  openToastRef.current = openToast;

  // Silence watchdog: one timer pair for the in-flight turn, re-armed on every
  // inbound frame and cleared when the turn ends (or on unmount).
  const watchdogRef = useRef<{
    still: ReturnType<typeof setTimeout> | null;
    stalled: ReturnType<typeof setTimeout> | null;
  }>({ still: null, stalled: null });

  const clearWatchdog = useCallback(() => {
    const timers = watchdogRef.current;
    if (timers.still !== null) clearTimeout(timers.still);
    if (timers.stalled !== null) clearTimeout(timers.stalled);
    timers.still = null;
    timers.stalled = null;
  }, []);

  const armWatchdog = useCallback(() => {
    clearWatchdog();
    const timers = watchdogRef.current;
    timers.still = setTimeout(() => {
      setActivityStatus(STILL_THINKING_TEXT);
    }, WATCHDOG_STILL_THINKING_MS);
    timers.stalled = setTimeout(() => {
      // Soft give-up — the socket stays up; Coach's own turn budget will end a
      // genuinely dead turn. The turn must return to idle so the composer
      // unlocks and the user can actually "try again" as the toast promises.
      setTurnState("idle");
      openToastRef.current({ variant: "error", message: STALLED_MESSAGE });
    }, WATCHDOG_STALLED_MS);
  }, [clearWatchdog]);

  const conversationIdRef = useRef<string | null>(null);
  // Latest Configuration/Thread/ConversationVersion seen from the server, for
  // "Coach Message Sent" (fired before any server response exists for the turn).
  const attributionRef = useRef<{
    configurationId: string | null;
    configurationName: string | null;
    threadId: string | null;
    threadLifecycle: string | null;
    conversationVersionId: string | null;
    conversationBlueprintName: string | null;
    conversationVersionNumber: number | null;
  }>({
    configurationId: null,
    configurationName: null,
    threadId: null,
    threadLifecycle: null,
    conversationVersionId: null,
    conversationBlueprintName: null,
    conversationVersionNumber: null,
  });
  const shouldShowGreetingRef = useRef(false);
  // Mirrors the ref for rendering. The effects below need the value
  // synchronously, which is why the ref is the source of truth — but the
  // welcome empty state has to know a greeting is coming, and a ref write
  // alone re-renders nothing. Always move the two together.
  const [isGreetingExpected, setIsGreetingExpected] = useState(false);
  const greetedRef = useRef(false);
  const starterHandledRef = useRef(false);

  const setShouldShowGreeting = useCallback((value: boolean) => {
    shouldShowGreetingRef.current = value;
    setIsGreetingExpected(value);
  }, []);

  // Live tracking of whether a QA session is active, read by reconcile /
  // reconnect guards so they never pull the main conversation into a QA thread.
  const qaActiveRef = useRef(false);
  qaActiveRef.current = Boolean(qaKey);

  // Per-conversation origin — see the starter effect and the first-message gate.
  const entryPointRef = useRef<string | null>(null);

  // Ref so the focus listener reads the latest value without re-subscribing.
  const turnStateRef = useRef<TurnState>("idle");
  turnStateRef.current = turnState;

  // Arm the watchdog whenever a turn starts (any path: send, greeting, retry)
  // and tear everything down whenever it ends. Per-event re-arms happen in
  // onEvent, since mid-stream events don't change turnState.
  useEffect(() => {
    if (turnState === "idle") {
      clearWatchdog();
      setActivityStatus(null);
    } else {
      armWatchdog();
    }
  }, [turnState, armWatchdog, clearWatchdog]);

  // Lets send/retry read the latest transcript without a `messages` dep.
  const messagesRef = useRef<Message[]>(messages);
  messagesRef.current = messages;

  const fetchChatStatus = useCallback(() => {
    // Send the browser timezone so Coach anchors the 4am-local day boundary
    // to the user's actual timezone (mirrors mobile). Without it Coach falls
    // back to UTC, merging a new day's chat into the previous day for users
    // west of UTC.
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    // Tells Coach not to stamp "opened" on status; reportViewed does that.
    const query = new URLSearchParams({ capabilities: "chat-viewed-v1" });
    if (timeZone) query.set("timezone", timeZone);
    return coachFetch<ChatStatusResponse>(`/api/chat/status/?${query}`);
  }, []);

  const reportViewed = useCallback(() => {
    if (document.visibilityState !== "visible") return;
    reportChatViewed().catch((error) =>
      logError("Ori chat viewed report failed", error)
    );
  }, []);

  // How many segments the open bounded thread had when its transcript was last
  // built, and a counter so a superseded load can't overwrite a later one.
  const qaSegmentCountRef = useRef(0);
  const qaLoadIdRef = useRef(0);

  // Build a bounded (QA) thread's transcript from every segment it holds,
  // oldest first, stamping each message with the agenda that produced it. The
  // *thread* is the unit here, not the conversation the tester opened: Ori can
  // move agenda mid-thread, and each move starts another segment.
  // Without `force`, a reload that finds no new segment leaves the live
  // transcript alone — only an agenda change can put a seam mid-transcript.
  const loadQaTranscript = useCallback(
    async (threadId: string, force: boolean) => {
      const loadId = (qaLoadIdRef.current += 1);
      const { conversations } = await fetchConversations({
        lifecycle: "bounded",
      });
      const segments = conversations
        .filter((conversation) => conversation.thread?.id === threadId)
        .sort((a, b) => a.started_at.localeCompare(b.started_at));
      if (!force && segments.length === qaSegmentCountRef.current) return;

      const details = await Promise.all(
        segments.map((segment) => fetchConversation(segment.id))
      );
      if (qaLoadIdRef.current !== loadId) return;
      qaSegmentCountRef.current = segments.length;
      conversationIdRef.current =
        segments.length > 0 ? segments[segments.length - 1].id : null;
      setMessages(
        details.flatMap((detail) =>
          detailToMessages(detail.messages, {
            type: detail.conversation_type,
            startedAt: detail.started_at,
          })
        )
      );
    },
    []
  );

  // Ori moving agenda is only visible to the client as a new segment on the
  // thread, so re-read the thread once a turn lands. Nothing to do on the
  // continuous chat, which reconciles through /api/chat/status/ instead.
  const reloadQaTranscript = useCallback(async () => {
    if (!qaThreadId) return;
    try {
      await loadQaTranscript(qaThreadId, false);
    } catch (error) {
      logError("Ori QA transcript reload failed", error);
    }
  }, [qaThreadId, loadQaTranscript]);
  const reloadQaTranscriptRef = useRef(reloadQaTranscript);
  reloadQaTranscriptRef.current = reloadQaTranscript;

  // Load history before releasing the connection gate. A failed fetch is the
  // one tolerated error: surface it and proceed fresh rather than wedge.
  const hasToken = Boolean(authToken);

  // QA session boundary: entering, switching, or exiting a QA test chat starts
  // a fresh thread. Declared BEFORE the history-load effect so that on exit
  // (qaKey "") this clears first and the load below repopulates the main
  // thread; on enter/switch it clears before the targeted thread's transcript
  // loads below. Never fires for non-QA sessions (qaKey stays "").
  const prevQaKeyRef = useRef(qaKey);
  useEffect(() => {
    if (prevQaKeyRef.current === qaKey) return;
    prevQaKeyRef.current = qaKey;
    greetedRef.current = false;
    starterHandledRef.current = false;
    setShouldShowGreeting(false);
    conversationIdRef.current = null;
    qaSegmentCountRef.current = 0;
    setTurnState("idle");
    setMessages([]);
    setThreadId(null);
  }, [qaKey, setShouldShowGreeting]);

  useEffect(() => {
    if (!hasToken) return undefined;

    let cancelled = false;
    setIsLoadingHistory(true);

    (async () => {
      try {
        if (qaThreadId) {
          // A QA chat resumes a specific bounded thread: seed the live
          // transcript from that thread's own segments (none for a
          // just-created thread), never the user's main history. A QA thread
          // never auto-greets — a resumed run continues where it left off,
          // and a new run's opener comes from the connection.
          setShouldShowGreeting(false);
          await loadQaTranscript(qaThreadId, true);
          return;
        }

        const status = await fetchChatStatus();
        if (cancelled) return;
        conversationIdRef.current = status.conversation_id ?? null;
        attributionRef.current = {
          configurationId: status.configuration_id ?? null,
          configurationName: status.configuration_name ?? null,
          threadId: status.thread_id ?? null,
          threadLifecycle: status.thread_lifecycle ?? null,
          conversationVersionId: status.conversation_version_id ?? null,
          conversationBlueprintName: status.conversation_blueprint_name ?? null,
          conversationVersionNumber: status.conversation_version_number ?? null,
        };
        setThreadId(status.thread_id ?? null);
        setShouldShowGreeting(Boolean(status.should_show_greeting));
        setHasEverMessaged(status.has_ever_messaged ?? true);
        setMessages(toMessages(status));
        reportViewed();
      } catch (error) {
        if (cancelled) return;
        if (error instanceof CoachAuthError) {
          authenticationFailed();
          return;
        }
        logError("Ori chat status fetch failed", error);
        openToastRef.current({
          variant: "error",
          message: "Couldn't load your conversation history",
        });
        conversationIdRef.current = null;
        setShouldShowGreeting(false);
      } finally {
        if (!cancelled) setIsLoadingHistory(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    hasToken,
    fetchChatStatus,
    reportViewed,
    qaThreadId,
    loadQaTranscript,
    setShouldShowGreeting,
  ]);

  // Schedule the next bounded auto-retry of the lock-rejected message. After
  // the cap, fall back to a soft "couldn't send" banner with the text retained
  // (no infinite retry, no message loss).
  const scheduleBusyRetry = useCallback(() => {
    const delay = BUSY_RETRY_BASE_MS * 2 ** busyAttemptRef.current;
    clearBusyRetryTimer();
    busyRetryTimerRef.current = setTimeout(() => {
      const pending = pendingRetryMessageRef.current;
      if (pending === null) return;
      if (busyAttemptRef.current >= BUSY_RETRY_MAX_ATTEMPTS) {
        // Out of attempts: stop retrying but keep the text so the user can
        // resend; surface a calm inline state rather than a red toast.
        clearBusyRetryTimer();
        setBusyPhase("could-not-send");
        return;
      }
      busyAttemptRef.current += 1;
      sendMessageRef.current(
        pending.text,
        lastServerMessageId(messagesRef.current),
        { tapIn: pending.tapIn }
      );
      setTurnState("sending");
      scheduleBusyRetry();
    }, delay);
  }, [clearBusyRetryTimer]);

  const onEvent = useCallback(
    (event: OriInboundEvent) => {
      // Any inbound frame proves the turn is alive — restart the silence clock.
      if (turnStateRef.current !== "idle") armWatchdog();
      switch (event.type) {
        case "typing_indicator":
          clearBusyState();
          setTurnState("sending");
          break;
        case "activity":
          setActivityStatus(event.text);
          break;
        case "message_start":
          clearBusyState();
          setActivityStatus(null);
          setTurnState("streaming");
          break;
        case "message_delta": {
          setActivityStatus(null);
          setTurnState("streaming");
          const { message_id, text } = event;
          setMessages((prev) => {
            const idx = prev.findIndex((m) => m.message_id === message_id);
            if (idx !== -1) {
              const next = [...prev];
              next[idx] = { ...next[idx], content: next[idx].content + text };
              return next;
            }
            const newBubble: Message = {
              role: "assistant",
              content: text,
              message_id,
            };
            const last = prev[prev.length - 1];
            if (last?.role === "assistant" && last.content === "") {
              return [...prev.slice(0, -1), newBubble];
            }
            return [...prev, newBubble];
          });
          break;
        }
        case "stream_interrupted": {
          const interruptedId = event.message_id;
          setMessages((prev) =>
            prev.filter((m) => m.message_id !== interruptedId)
          );
          setTurnState("idle");
          openToastRef.current({
            variant: "error",
            message: INTERRUPTED_MESSAGE,
          });
          break;
        }
        case "message_discard": {
          const discardId = event.message_id;
          setMessages((prev) => prev.filter((m) => m.message_id !== discardId));
          break;
        }
        case "message_chunk":
          clearBusyState();
          setActivityStatus(null);
          setTurnState("streaming");
          if (event.content) {
            setThreadId((previous) => event.thread_id ?? previous);
            attributionRef.current = {
              configurationId: event.configuration_id ?? null,
              configurationName: event.configuration_name ?? null,
              threadId: event.thread_id ?? null,
              threadLifecycle: event.thread_lifecycle ?? null,
              conversationVersionId: event.conversation_version_id ?? null,
              conversationBlueprintName:
                event.conversation_blueprint_name ?? null,
              conversationVersionNumber:
                event.conversation_version_number ?? null,
            };
            const incoming: Message = {
              role: "assistant",
              content: event.content,
              message_id: event.message_id,
              embedded_ui: event.embedded_ui,
              ...parseOptions(event),
              conversation_id: event.conversation_id,
              configuration_id: event.configuration_id,
              configuration_name: event.configuration_name,
              thread_id: event.thread_id,
              thread_lifecycle: event.thread_lifecycle,
              conversation_version_id: event.conversation_version_id,
              conversation_blueprint_name: event.conversation_blueprint_name,
              conversation_version_number: event.conversation_version_number,
            };
            setMessages((prev) => {
              const idx = incoming.message_id
                ? prev.findIndex((m) => m.message_id === incoming.message_id)
                : -1;
              if (idx === -1) {
                trackMessageReceived(incoming, prev.length);
                return [...prev, incoming];
              }
              trackMessageReceived(incoming, idx);
              const next = [...prev];
              next[idx] = incoming;
              return next;
            });
          }
          break;
        case "complete": {
          clearBusyState();
          setTurnState("idle");
          // Coach Message Received fires at message_chunk (the only event that
          // carries reply content); a content-bearing reply always produces one.
          void reloadQaTranscriptRef.current();
          break;
        }
        case "error":
        case "capacity_error": {
          if (isLockRejection(event)) {
            // Soft cross-device state: another socket holds the lock. Hold the
            // pending text and auto-retry on a bounded backoff instead of a
            // red toast.
            setTurnState("idle");
            setBusyPhase("retrying");
            scheduleBusyRetry();
            break;
          }
          setTurnState("idle");
          // The turn is over and it produced nothing — an errored greeting
          // inserts no message, only this toast, so without this the
          // transcript stays empty with a greeting still marked pending and
          // the welcome would stay hidden behind a blank message area.
          // Nothing is coming any more, so let the welcome stand in
          // (WONE-2709).
          setShouldShowGreeting(false);
          openToastRef.current({
            variant: "error",
            message:
              event.message ||
              ("error" in event && event.error) ||
              (event.type === "capacity_error"
                ? "Ori is busy right now"
                : "Something went wrong"),
          });
          break;
        }
        default:
          break;
      }
    },
    [armWatchdog, clearBusyState, scheduleBusyRetry, setShouldShowGreeting]
  );

  const { connectionState, isConnected, reconnect, sendMessage } =
    useOriConnection({ onEvent, enabled: enabled && !isLoadingHistory, qa });
  sendMessageRef.current = sendMessage;

  useEffect(
    () => () => {
      clearBusyRetryTimer();
      clearWatchdog();
    },
    [clearBusyRetryTimer, clearWatchdog]
  );

  // A stashed starter prompt sends on connect regardless of history (unlike the
  // greeting), and marks the turn greeted so __START__ can't also fire. Declared
  // before the greeting effect so that suppression is in place when it runs;
  // reaches `send` (defined below) through a ref to avoid a forward reference.
  const sendRef = useRef<
    (text: string, source?: InputSource, tapIn?: TapInWire) => void
  >(() => {});

  useEffect(() => {
    if (!isConnected || starterHandledRef.current) return;
    starterHandledRef.current = true;
    const stashed = consumeOriStarterMessage();
    setHasPendingEntryStarter(false);
    // Set the origin only from a real stash: this re-runs on remount/reconnect,
    // when the carrier is already consumed, and must not overwrite it then.
    if (stashed) {
      entryPointRef.current = stashed.entryPoint;
    }
    const starter = stashed?.text.trim();
    if (starter) {
      greetedRef.current = true;
      recordStarterSent(starter);
      // Only a tap-in's stash carries wire fields. Other stashes ("direct",
      // the nav tab, the home score hook) name an origin for analytics alone.
      const tapIn =
        stashed?.context &&
        (TAP_IN_ENTRY_POINTS as readonly string[]).includes(stashed.entryPoint)
          ? {
              entryPoint: stashed.entryPoint as TapInEntryPoint,
              context: stashed.context,
            }
          : undefined;
      sendRef.current(starter, "keyboard", tapIn);
    }
  }, [isConnected]);

  useEffect(() => {
    if (
      isConnected &&
      flagsResolved &&
      shouldShowGreetingRef.current &&
      messages.length === 0 &&
      !greetedRef.current
    ) {
      greetedRef.current = true;
      sendMessage(GREETING_TRIGGER, null, {
        capabilities: startersEnabled
          ? [STARTER_OPTIONS_CAPABILITY]
          : undefined,
      });
      setTurnState("sending");
    }
  }, [
    isConnected,
    flagsResolved,
    startersEnabled,
    messages.length,
    sendMessage,
  ]);

  // Reconcile against authoritative server state on tab focus / reconnect.
  const reconcileStatus = useCallback(async () => {
    // A QA test chat is live-only; reconciling would pull the user's main
    // conversation (qa_test is excluded from /api/chat/status/ resolution).
    if (qaActiveRef.current) return;
    let status: ChatStatusResponse;
    try {
      status = await fetchChatStatus();
    } catch (error) {
      if (error instanceof CoachAuthError) {
        authenticationFailed();
        return;
      }
      logError("Ori chat status refetch failed", error);
      return;
    }

    const incomingId = status.conversation_id ?? null;
    const restMessages = toMessages(status);
    attributionRef.current = {
      configurationId: status.configuration_id ?? null,
      configurationName: status.configuration_name ?? null,
      threadId: status.thread_id ?? null,
      threadLifecycle: status.thread_lifecycle ?? null,
      conversationVersionId: status.conversation_version_id ?? null,
      conversationBlueprintName: status.conversation_blueprint_name ?? null,
      conversationVersionNumber: status.conversation_version_number ?? null,
    };
    setThreadId(status.thread_id ?? null);
    setShouldShowGreeting(Boolean(status.should_show_greeting));
    setHasEverMessaged(status.has_ever_messaged ?? true);

    if (incomingId === conversationIdRef.current) {
      // Same conversation: REST wins, replacing any locally-echoed bubbles.
      setMessages(restMessages);
      reportViewed();
      return;
    }

    // New/completed conversation: adopt the id and re-arm the greeting (and a
    // freshly-stashed starter, so a second CTA click is honoured).
    conversationIdRef.current = incomingId;
    greetedRef.current = false;
    starterHandledRef.current = false;
    setMessages(restMessages);
    reportViewed();
  }, [fetchChatStatus, reportViewed, setShouldShowGreeting]);

  const reconcileStatusRef = useRef(reconcileStatus);
  reconcileStatusRef.current = reconcileStatus;

  useEffect(() => {
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    const scheduleReconcile = () => {
      if (debounceTimer !== null) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        // Never clobber an in-flight turn — only reconcile while idle.
        if (turnStateRef.current !== "idle") return;
        void reconcileStatusRef.current();
      }, FOCUS_REFETCH_DEBOUNCE_MS);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      scheduleReconcile();
    };

    // window focus catches switching between two visible windows, which
    // visibilitychange misses; focus implies visible so it needs no guard.
    const onFocus = () => scheduleReconcile();

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", onFocus);
    return () => {
      if (debounceTimer !== null) clearTimeout(debounceTimer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  const send = useCallback(
    (text: string, source: InputSource = "keyboard", tapIn?: TapInWire) => {
      const trimmed = text.trim();
      if (!trimmed) return;

      // Technical sends (__START__/greeting) aren't user turns — no analytics.
      if (!trimmed.startsWith("__")) {
        // Entry Point rides only the first message of a fresh conversation: a
        // consumed stash wins, else "direct" on an empty transcript. A remount
        // of an existing conversation has neither, so it can't leak the origin.
        const entryPoint =
          entryPointRef.current ??
          (messagesRef.current.length === 0 ? "direct" : null);
        const attribution = attributionRef.current;
        trackEvent("Coach Message Sent", {
          "Message Index": messagesRef.current.length,
          "Input Source": source,
          "Configuration ID": attribution.configurationId,
          "Configuration Name": attribution.configurationName,
          "Thread ID": attribution.threadId,
          "Thread Lifecycle": attribution.threadLifecycle,
          "Conversation Version ID": attribution.conversationVersionId,
          "Conversation Blueprint Name": attribution.conversationBlueprintName,
          "Conversation Version Number": attribution.conversationVersionNumber,
          ...(entryPoint !== null && {
            "Entry Point": entryPoint,
          }),
        });
        entryPointRef.current = null;
      }

      // Retain the text so a lock rejection can auto-retry it without the user
      // losing what they typed; a fresh send supersedes any prior pending one.
      pendingRetryMessageRef.current = { text: trimmed, tapIn };
      busyAttemptRef.current = 0;
      setBusyPhase("idle");
      const anchor = lastServerMessageId(messagesRef.current);
      setMessages((prev) => [...prev, { role: "user", content: trimmed }]);
      sendMessage(trimmed, anchor, { tapIn });
      setTurnState("sending");
    },
    [sendMessage]
  );
  sendRef.current = send;

  // Retry re-arms the greeting and refetches. Clear the greeting flag so a
  // failed refetch can't re-greet off a stale value (a successful refetch
  // resets it). Don't pre-clear messages: reconcile replaces them on success,
  // so a failed refetch leaves the existing transcript instead of wiping it.
  const handleReconnect = useCallback(() => {
    greetedRef.current = false;
    starterHandledRef.current = false;
    setShouldShowGreeting(false);
    setTurnState("idle");
    if (qaActiveRef.current) {
      // QA reset: the server archived the qa_test conversation; start a fresh
      // QA thread and reconnect. Never reconcile against the main conversation.
      setMessages([]);
      reconnect();
      return;
    }
    reconnect();
    void reconcileStatusRef.current();
  }, [reconnect, setShouldShowGreeting]);

  return {
    messages,
    threadId,
    sendMessage: send,
    hasEverMessaged,
    hasPendingEntryStarter,
    isLoadingHistory,
    // A send-lock can't outlive the socket, or the composer wedges on a drop.
    isSending: isConnected && turnState !== "idle",
    isAwaitingReply: isConnected && turnState === "sending",
    busyMessage: busyPhase === "idle" ? null : BUSY_MESSAGE[busyPhase],
    // Only meaningful while a turn is in flight; a stale line can't outlive
    // the turn because the idle transition clears it.
    activityStatus: turnState === "idle" ? null : activityStatus,
    connectionState,
    isConnected,
    // A greeting is on its way: the server says this conversation opens with
    // one, and the socket that will carry it hasn't given up. An empty
    // transcript then means "not known yet", not "nothing here" — so the
    // welcome must stay hidden until the greeting's typing bubble lands.
    // Without this the welcome flashes for the whole socket handshake, which
    // only starts once history has settled (WONE-2709).
    isGreetingPending: isGreetingExpected && connectionState !== "error",
    reconnect: handleReconnect,
  };
}
